import { useState, useEffect } from "react";
import {
  BarChart, Bar, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line
} from "recharts";
import { PLAYERS } from "../data/players";

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const genHistory = (base, weeks = 24) => {
  const data = []; let val = base - 7;
  for (let i = 0; i < weeks; i++) {
    val += (Math.random() - 0.36) * 2.0;
    val = Math.min(99, Math.max(65, val));
    data.push({
      week: `W${i + 1}`,
      rating: parseFloat(val.toFixed(1)),
      volume: Math.floor(Math.random() * 500 + 80),
      composite: parseFloat((val + (Math.random() - 0.5) * 2.5).toFixed(1)),
    });
  }
  data[data.length - 1].rating = base;
  return data;
};

// Cache generated histories so they don't re-randomize on every render
const historyCache = {};
const getHistory = (id, rating) => {
  if (!historyCache[id]) historyCache[id] = genHistory(rating);
  return historyCache[id];
};

const TEAMS = [
  { id: 10, name: "LSU Tigers", abbr: "LSU", conf: "SEC", rating: 96, delta: +2.8 },
  { id: 11, name: "Michigan", abbr: "MICH", conf: "Big Ten", rating: 93, delta: +1.4 },
  { id: 12, name: "Oregon", abbr: "ORE", conf: "Big Ten", rating: 92, delta: +1.9 },
  { id: 13, name: "Baylor", abbr: "BAY", conf: "Big 12", rating: 84, delta: +4.2 },
];

const PORTAL_FEED = [
  { id: 1, player: "DJ Lagway", pos: "QB", from: "Florida", to: "Baylor", date: "Jan 18", status: "Enrolled", rating: 92, stars: 5 },
  { id: 2, player: "Sam Leavitt", pos: "QB", from: "Arizona State", to: "LSU", date: "Jan 12", status: "Enrolled", rating: 88, stars: 4 },
  { id: 3, player: "Dylan Raiola", pos: "QB", from: "Nebraska", to: "Oregon", date: "Jan 9", status: "Enrolled", rating: 87, stars: 5 },
  { id: 4, player: "Jordan Seaton", pos: "OT", from: "Colorado", to: "LSU", date: "Jan 14", status: "Enrolled", rating: 95, stars: 5 },
  { id: 5, player: "John Henry Daley", pos: "EDGE", from: "Utah", to: "Michigan", date: "Jan 10", status: "Enrolled", rating: 94, stars: 3 },
  { id: 6, player: "Princewill Umanmielen", pos: "EDGE", from: "Ole Miss", to: "LSU", date: "Jan 21", status: "Enrolled", rating: 91, stars: 4 },
];

const NEWS = [
  { id: 1, time: "2h", source: "GridironIQ", tag: "Transfer", sentiment: "positive", headline: "Lagway officially signs with Baylor — highest-rated recruit in program history", body: "DJ Lagway's commitment ends a whirlwind recruitment that included visits to Ole Miss, Florida State, and Virginia. Dave Aranda lands his franchise QB." },
  { id: 2, time: "4h", source: "GridironIQ", tag: "Analysis", sentiment: "positive", headline: "John Henry Daley leads all portal defenders with 92.5 pass-rush grade", body: "Daley's 51 pressures on 280 pass-rush snaps translates to a pressure rate that ranked in the top 3% of all FBS edge rushers in 2025." },
  { id: 3, time: "6h", source: "GridironIQ", tag: "Recruiting", sentiment: "neutral", headline: "Jordan Seaton's LSU commitment continues portal-driven rebuild", body: "The #1 OT in the 2024 class gives LSU an immediate plug-and-play left tackle for the first time in years. Three-year draft projection." },
  { id: 4, time: "8h", source: "ESPN", tag: "Injury", sentiment: "negative", headline: "Sam Leavitt expected full recovery from Lisfranc — spring timeline confirmed", body: "LSU medical staff confirmed Leavitt is on track for full participation in spring practice. The injury occurred in Week 6 of the 2025 season." },
  { id: 5, time: "10h", source: "The Athletic", tag: "NIL", sentiment: "positive", headline: "Umanmielen portal saga ends — LSU pays reported $550K buyout to Ole Miss", body: "The contested portal entry drew national attention after Ole Miss publicly announced Umanmielen's return before he reversed course on the final day of the window." },
  { id: 6, time: "12h", source: "GridironIQ", tag: "Analysis", sentiment: "neutral", headline: "Dylan Raiola gives Oregon a pocket passer to complement Dante Moore in spring competition", body: "Raiola's 4,819 yards over two seasons at Nebraska gives Oregon options. Spring depth chart battle expected to be one of the most-watched in the country." },
];

const sentimentBar = { positive: "#166534", negative: "#991b1b", neutral: "#374151" };

// ─── STAT RENDERERS BY POSITION ───────────────────────────────────────────────

const getStatBoxes = (player, audience) => {
  const p = player.production;
  const m = player.measurables;
  const pos = player.pos;

  if (audience === "staff") {
    if (pos === "QB") return [
      { label: "Pass Yards", value: p.twoyearYards ? `${p.twoyearYards.toLocaleString()} (2yr)` : p.yards?.toLocaleString() || "—" },
      { label: "TD / INT", value: p.twoyearTDs ? `${p.twoyearTDs} / ${p.twoyearINTs}` : `${p.tds} / ${p.ints}`, accent: (p.ints || p.twoyearINTs) > 15 ? "#991b1b" : "#166534" },
      { label: "Comp %", value: p.compPct ? `${p.compPct}%` : "—" },
      { label: "Grade", value: p.pffGrade || p.pffGrade2024 || "—", accent: (p.pffGrade || p.pffGrade2024) >= 80 ? "#166534" : "#991b1b" },
      { label: "Snap Count", value: p.snapCount?.toLocaleString() || "—" },
      { label: "Eligibility", value: player.eligibility },
    ];
    if (pos === "EDGE") return [
      { label: "Sacks", value: p.sacks || "—", accent: "#991b1b" },
      { label: "TFL", value: p.tfl || "—" },
      { label: "Pressures", value: p.pressures || "—" },
      { label: "Grade", value: p.pffGrade || "—", accent: p.pffGrade >= 85 ? "#166534" : "#374151" },
      { label: "Snap Count", value: p.snapCount?.toLocaleString() || "—" },
      { label: "Eligibility", value: player.eligibility },
    ];
    if (pos === "OT") return [
      { label: "Sacks Allowed", value: p.sacksAllowed ?? "—", accent: p.sacksAllowed <= 2 ? "#166534" : "#991b1b" },
      { label: "Pressures Allowed", value: p.pressuresAllowed ?? "—" },
      { label: "Pass Block Snaps", value: p.snapCount?.toLocaleString() || p.season2024SnapCount?.toLocaleString() || "—" },
      { label: "Grade", value: p.pffGrade || p.season2024PffGrade || "—", accent: "#166534" },
      { label: "Height / Weight", value: `${m.height} / ${m.weight}lbs` },
      { label: "Eligibility", value: player.eligibility },
    ];
  }

  // Media audience
  if (pos === "QB") return [
    { label: "Pass Yards", value: p.twoyearYards ? `${p.twoyearYards.toLocaleString()} (2yr)` : p.yards?.toLocaleString() || "—" },
    { label: "TD / INT", value: p.twoyearTDs ? `${p.twoyearTDs} / ${p.twoyearINTs}` : `${p.tds} / ${p.ints}` },
    { label: "Stars", value: `${"★".repeat(player.recruiting.stars247)}`, accent: "#854d0e" },
    { label: "Natl. Rank", value: player.recruiting.nationalRank247 ? `#${player.recruiting.nationalRank247}` : "—", accent: "#0f0f0f" },
    { label: "Pos. Rank", value: player.recruiting.posRank247 ? `#${player.recruiting.posRank247} ${pos}` : "—" },
    { label: "Class", value: player.recruiting.classYear },
  ];
  if (pos === "EDGE") return [
    { label: "Sacks", value: p.sacks || "—", accent: "#991b1b" },
    { label: "TFL", value: p.tfl || "—" },
    { label: "Stars", value: `${"★".repeat(player.recruiting.stars247)}`, accent: "#854d0e" },
    { label: "Pos. Rank", value: player.recruiting.posRank247 ? `#${player.recruiting.posRank247}` : "—" },
    { label: "Prior School", value: player.prev },
    { label: "Destination", value: player.destination, accent: "#166534" },
  ];
  if (pos === "OT") return [
    { label: "Sacks Allowed", value: p.sacksAllowed ?? "—", accent: "#166534" },
    { label: "Stars", value: `${"★".repeat(player.recruiting.stars247)}`, accent: "#854d0e" },
    { label: "Natl. Rank", value: player.recruiting.nationalRank247 ? `#${player.recruiting.nationalRank247}` : "—" },
    { label: "Pos. Rank", value: `#${player.recruiting.posRank247} ${pos}` },
    { label: "Prior School", value: player.prev },
    { label: "Destination", value: player.destination, accent: "#166534" },
  ];
  return [];
};

const getWeeklyHeaders = (pos) => {
  if (pos === "QB") return ["Week", "Yards", "TDs", "INTs"];
  if (pos === "EDGE") return ["Week", "Sacks", "Pressures"];
  if (pos === "OT") return ["Week", "Sacks Allowed", "Pressures Allowed"];
  return ["Week", "Stat 1", "Stat 2"];
};

const getWeeklyRow = (s, pos) => {
  if (pos === "QB") return [s.yards, <span style={{ color: "#166534", fontWeight: 700 }}>{s.tds}</span>, <span style={{ color: "#991b1b" }}>{s.ints}</span>];
  if (pos === "EDGE") return [<span style={{ color: "#991b1b", fontWeight: 700 }}>{s.sacks}</span>, s.pressures];
  if (pos === "OT") return [<span style={{ color: s.sacksAllowed === 0 ? "#166534" : "#991b1b", fontWeight: 700 }}>{s.sacksAllowed}</span>, s.pressures];
  return [];
};

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────

const PosBadge = ({ pos }) => (
  <span style={{ background: "#0f0f0f", color: "#fff", fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "2px", letterSpacing: "0.08em" }}>{pos}</span>
);

const StatusPill = ({ status }) => {
  const colors = { Available: { bg: "#fefce8", text: "#854d0e", border: "#ca8a04" }, Enrolled: { bg: "#f0fdf4", text: "#166534", border: "#16a34a" } };
  const c = colors[status] || colors.Available;
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", fontWeight: 700, padding: "2px 7px", borderRadius: "2px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{status}</span>;
};

const MiniSparkline = ({ data, up }) => (
  <ResponsiveContainer width={64} height={24}>
    <LineChart data={data.slice(-10)}>
      <Line type="monotone" dataKey="rating" stroke={up ? "#166534" : "#991b1b"} strokeWidth={1.5} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "3px", padding: "8px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af", marginBottom: "3px" }}>{label}</div>
      {payload.map((p, i) => <div key={i} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: "#0f0f0f", fontWeight: 700 }}>{p.name}: {typeof p.value === "number" ? p.value.toFixed(1) : p.value}</div>)}
    </div>
  );
};

const Ticker = ({ items }) => {
  const [offset, setOffset] = useState(0);
  useEffect(() => { const id = setInterval(() => setOffset(o => o - 0.5), 20); return () => clearInterval(id); }, []);
  const totalW = 3200;
  const adj = ((offset % totalW) + totalW) % totalW;
  // Triple items so ticker never runs empty regardless of content width
  const displayed = [...items, ...items, ...items, ...items];
  return (
    <div style={{ overflow: "hidden", height: "28px", background: "#0f0f0f", position: "relative", flexShrink: 0 }}>
      <div style={{ display: "flex", position: "absolute", left: -adj, whiteSpace: "nowrap", alignItems: "center", height: "100%" }}>
        {displayed.map((item, i) => (
          <span key={i} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", letterSpacing: "0.04em", padding: "0 24px", borderRight: "1px solid #2a2a2a", color: "#ccc" }}>
            <span style={{ color: "#666", marginRight: "7px" }}>{item.pos}</span>
            <span style={{ color: "#e5e5e5", marginRight: "7px", fontWeight: 600 }}>{item.name}</span>
            <span style={{ color: "#555", marginRight: "5px" }}>→</span>
            <span style={{ color: "#4ade80" }}>{item.destination}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div style={{ background: "#f8f7f4", border: "1px solid #e5e7eb", borderRadius: "3px", padding: "10px 14px", flex: 1, minWidth: "80px" }}>
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "4px" }}>{label}</div>
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "15px", fontWeight: 700, color: accent || "#0f0f0f" }}>{value}</div>
  </div>
);

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [selected, setSelected] = useState(PLAYERS[0]);
  const [tab, setTab] = useState("chart");
  const [view, setView] = useState("dashboard");
  const [feedFilter, setFeedFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [time, setTime] = useState(new Date());
  const [audience, setAudience] = useState("production");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { const id = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(id); }, []);

  const isProduction = audience === "production";
  const statBoxes = getStatBoxes(selected, isProduction ? "staff" : "media");
  const filteredNews = feedFilter === "ALL" ? NEWS : NEWS.filter(n => n.tag === feedFilter);
  const filteredPortal = PORTAL_FEED.filter(p => {
    const q = search.toLowerCase();
    return p.player.toLowerCase().includes(q) || p.pos.toLowerCase().includes(q) || p.from.toLowerCase().includes(q);
  });

  const C = {
    root: { height: "100vh", display: "flex", flexDirection: "column", background: "#f8f7f4", color: "#0f0f0f", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "13px", overflow: "hidden" },
    header: { background: "#0f0f0f", height: "48px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0 },
    logo: { fontFamily: "'Playfair Display', serif", fontSize: "18px", fontWeight: 900, color: "#fff", letterSpacing: "-0.3px" },
    logoSub: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#555", marginLeft: "10px", letterSpacing: "0.16em" },
    navBtn: (active) => ({ background: active ? "#fff" : "transparent", color: active ? "#0f0f0f" : "#fff", border: active ? "none" : "none", padding: "5px 14px", borderRadius: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: active ? 700 : 400, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.15s" }),
    audienceBtn: (active) => ({ background: active ? "#fff" : "transparent", color: active ? "#0f0f0f" : "#555", border: active ? "none" : "1px solid #333", padding: "4px 12px", borderRadius: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.15s" }),
    body: { display: "flex", flex: 1, overflow: "hidden" },
    sidebar: { width: "200px", flexShrink: 0, background: "#fff", borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column", overflow: "hidden" },
    sideLabel: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.16em", padding: "10px 14px 5px", borderBottom: "1px solid #f3f4f6" },
    watchRow: (active) => ({ display: "flex", alignItems: "center", padding: "8px 14px", background: active ? "#f8f7f4" : "#fff", borderLeft: active ? "2px solid #0f0f0f" : "2px solid transparent", cursor: "pointer" }),
    main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f8f7f4" },
    mainTop: { background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 20px 14px", flexShrink: 0 },
    mainBody: { flex: 1, overflowY: "auto", padding: "16px 20px" },
    right: { width: "280px", flexShrink: 0, background: "#fff", borderLeft: "1px solid #e5e7eb", display: "flex", flexDirection: "column", overflow: "hidden" },
    rightHeader: { padding: "10px 14px", borderBottom: "1px solid #f3f4f6", fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.16em", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 },
    card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "3px", padding: "16px" },
    tab: (active) => ({ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", padding: "4px 12px", background: active ? "#0f0f0f" : "transparent", color: active ? "#fff" : "#9ca3af", border: active ? "1px solid #0f0f0f" : "1px solid #e5e7eb", borderRadius: "2px", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700 }),
  };

  return (
    <div style={C.root} className="mobile-root">
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;600;700&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 3px; } ::-webkit-scrollbar-track { background: #f3f4f6; } ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .watch-row:hover { background: #f8f7f4 !important; }
        .portal-row:hover { background: #f8f7f4 !important; }
        .news-card:hover { background: #f8f7f4 !important; }
        .mobile-menu-btn { display: none !important; }
        @media (max-width: 768px) {
          .sidebar-panel { display: none !important; }
          .right-panel { display: none !important; }
          .main-panel { width: 100% !important; min-height: 0 !important; }
          .header-nav { display: none !important; }
          .header-meta { gap: 6px !important; }
          .header-spring { display: none !important; }
          .stat-strip { flex-wrap: wrap !important; }
          .stat-strip > div { min-width: 100px !important; flex: 1 1 calc(33% - 6px) !important; }
          .player-header { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .tab-bar { overflow-x: auto !important; flex-wrap: nowrap !important; padding-bottom: 4px !important; }
          .tab-bar::-webkit-scrollbar { height: 0; }
          .tab-bar button { flex-shrink: 0 !important; white-space: nowrap !important; font-size: 9px !important; padding: 5px 10px !important; }
          .mobile-feed { display: block !important; }
          .mobile-clock-row { flex-direction: column !important; align-items: flex-end !important; gap: 1px !important; }
          .mobile-spring { display: block !important; }
          .mobile-menu-btn { display: flex !important; }
          .mobile-root { height: auto !important; overflow: visible !important; }
          .mobile-body { overflow: visible !important; flex-direction: column !important; }
          .mobile-main { overflow: visible !important; height: auto !important; }
          .mobile-main-body { overflow: visible !important; flex: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-spring { display: none !important; }
          .mobile-feed { display: none !important; }
        }
      `}</style>

      {/* ── MASTHEAD ── */}
      <header style={C.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Hamburger — mobile only */}
          <button className="mobile-menu-btn" onClick={() => setDrawerOpen(true)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", flexDirection: "column", gap: "4px", alignItems: "center", justifyContent: "center" }}>
            <span style={{ display: "block", width: "18px", height: "2px", background: "#fff", borderRadius: "1px" }} />
            <span style={{ display: "block", width: "18px", height: "2px", background: "#fff", borderRadius: "1px", margin: "3px 0" }} />
            <span style={{ display: "block", width: "18px", height: "2px", background: "#fff", borderRadius: "1px" }} />
          </button>
          <div style={C.logo}>GridironIQ</div>
        </div>
        <nav style={{ display: "flex", gap: "4px" }} className="header-nav">
          <button style={C.navBtn(view === "dashboard")} onClick={() => setView("dashboard")}>Dashboard</button>
          <button style={C.navBtn(view === "portal")} onClick={() => setView("portal")}>Portal</button>
          <button style={C.navBtn(false)}>Board</button>
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }} className="header-meta">
          <div style={{ display: "flex", gap: "3px", background: "#1a1a1a", padding: "3px", borderRadius: "3px" }}>
            <button style={C.audienceBtn(audience === "production")} onClick={() => setAudience("production")}>Production</button>
            <button style={C.audienceBtn(audience === "profile")} onClick={() => setAudience("profile")}>Profile</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#4ade80" }} />
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#555" }}>LIVE</span>
          </div>
          {/* Clock — on mobile shows SPRING 2026 underneath */}
          <div className="mobile-clock-row" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#555" }}>{time.toLocaleTimeString("en-US", { hour12: false })} ET</span>
            <span className="mobile-spring" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#444", display: "none" }}>SPRING 2026</span>
          </div>
          <span className="header-spring" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#555", padding: "3px 8px", border: "1px solid #333", borderRadius: "2px" }}>SPRING 2026</span>
        </div>
      </header>

      <Ticker items={PLAYERS} />

      {/* ── MOBILE DRAWER ── */}
      {drawerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000 }}>
          {/* Backdrop */}
          <div onClick={() => setDrawerOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
          {/* Drawer */}
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "280px", background: "#0f0f0f", display: "flex", flexDirection: "column", animation: "slideIn 0.2s ease", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #1f1f1f" }}>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 900, color: "#fff" }}>GridironIQ</span>
              <button onClick={() => setDrawerOpen(false)} style={{ background: "transparent", border: "none", color: "#888", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}>✕</button>
            </div>

            {/* View nav */}
            <div style={{ padding: "16px 20px 8px" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#555", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: "10px" }}>Navigate</div>
              {[["dashboard", "Dashboard"], ["portal", "Portal"]].map(([v, l]) => (
                <button key={v} onClick={() => { setView(v); setDrawerOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", background: view === v ? "#1a1a1a" : "transparent", border: "none", color: view === v ? "#fff" : "#888", fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", fontWeight: view === v ? 700 : 400, padding: "10px 12px", borderRadius: "3px", cursor: "pointer", marginBottom: "2px", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  {view === v ? "→ " : ""}{l}
                </button>
              ))}
            </div>

            <div style={{ height: "1px", background: "#1f1f1f", margin: "4px 0" }} />

            {/* Player select */}
            <div style={{ padding: "12px 20px 8px" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#555", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: "10px" }}>Players</div>
              {PLAYERS.map(p => (
                <button key={p.id} onClick={() => { setSelected(p); setView("dashboard"); setTab("chart"); setDrawerOpen(false); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", textAlign: "left", background: selected.id === p.id ? "#1a1a1a" : "transparent", border: "none", color: selected.id === p.id ? "#fff" : "#888", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", fontWeight: selected.id === p.id ? 700 : 400, padding: "9px 12px", borderRadius: "3px", cursor: "pointer", marginBottom: "2px" }}>
                  <span>{p.name}</span>
                  <span style={{ fontSize: "9px", color: "#555" }}>{p.pos} · {p.destination}</span>
                </button>
              ))}
            </div>

            <div style={{ height: "1px", background: "#1f1f1f", margin: "4px 0" }} />

            {/* View toggle */}
            <div style={{ padding: "12px 20px" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#555", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: "10px" }}>View Mode</div>
              {[["production", "Production"], ["profile", "Profile"]].map(([a, l]) => (
                <button key={a} onClick={() => { setAudience(a); setDrawerOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", background: audience === a ? "#1a1a1a" : "transparent", border: "none", color: audience === a ? "#4ade80" : "#888", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", fontWeight: audience === a ? 700 : 400, padding: "9px 12px", borderRadius: "3px", cursor: "pointer", marginBottom: "2px" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={C.body} className="mobile-body">

        {/* ── SIDEBAR ── */}
        <aside style={C.sidebar} className="sidebar-panel">
          <div style={C.sideLabel}>Watchlist — Players</div>
          {PLAYERS.map(p => {
            const hist = getHistory(p.id, p.rating);
            const active = selected.id === p.id;
            return (
              <div key={p.id} className="watch-row" style={C.watchRow(active)} onClick={() => { setSelected(p); setView("dashboard"); setTab("chart"); }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#0f0f0f", marginBottom: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name.split(" ")[0][0]}. {p.name.split(" ").slice(1).join(" ")}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af" }}>{p.pos} · {p.destination}</div>
                </div>
                <div style={{ textAlign: "right", marginRight: "4px" }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af" }}>{p.eligibility}</div>
                </div>
                <MiniSparkline data={hist} up={p.delta >= 0} />
              </div>
            );
          })}

          <div style={C.sideLabel}>Watchlist — Teams</div>
          {TEAMS.map(t => (
            <div key={t.id} className="watch-row" style={C.watchRow(false)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", fontWeight: 700, color: "#0f0f0f" }}>{t.abbr}</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af" }}>{t.conf}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af" }}>{t.conf}</div>
              </div>
            </div>
          ))}

          <div style={{ marginTop: "auto", padding: "12px 14px", borderTop: "1px solid #f3f4f6" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "8px" }}>Portal 2026</div>
            {[{ l: "Available", v: "512", c: "#854d0e" }, { l: "Enrolled", v: "2,688", c: "#166534" }, { l: "Total", v: "3,200+", c: "#0f0f0f" }].map(s => (
              <div key={s.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af" }}>{s.l}</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 700, color: s.c }}>{s.v}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* ── PORTAL VIEW ── */}
        {view === "portal" && (
          <main style={C.main}>
            <div style={C.mainTop}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "22px", fontWeight: 700, color: "#0f0f0f", marginBottom: "2px" }}>Transfer Portal</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af" }}>Winter 2026 · {PORTAL_FEED.length} tracked entries · Jan 2–16 window closed</div>
                </div>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search player, position, school..."
                  style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", padding: "7px 12px", border: "1px solid #e5e7eb", borderRadius: "3px", background: "#f8f7f4", color: "#0f0f0f", width: "260px", outline: "none" }}
                />
              </div>
            </div>
            <div style={C.mainBody}>
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "3px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8f7f4", borderBottom: "2px solid #0f0f0f" }}>
                      {["Player", "Pos", "Stars", "From", "Destination", "Date", "Status"].map(h => (
                        <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#0f0f0f", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPortal.map(p => (
                      <tr key={p.id} className="portal-row" style={{ borderBottom: "1px solid #f3f4f6", background: "#fff", cursor: "pointer" }}
                        onClick={() => { const match = PLAYERS.find(pl => pl.name === p.player); if (match) { setSelected(match); setView("dashboard"); } }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: "12px", color: "#0f0f0f" }}>{p.player}</td>
                        <td style={{ padding: "10px 14px" }}><PosBadge pos={p.pos} /></td>
                        <td style={{ padding: "10px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: "#854d0e", fontWeight: 700 }}>{"★".repeat(p.stars)}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: "#6b7280" }}>{p.from}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: "#166534", fontWeight: 700 }}>{p.to || "—"}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", color: "#9ca3af" }}>{p.date}</td>
                        <td style={{ padding: "10px 14px" }}><StatusPill status={p.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        )}

        {/* ── DASHBOARD VIEW ── */}
        {view === "dashboard" && (
          <main style={C.main} className="main-panel mobile-main">
            <div style={C.mainTop}>
              {/* Audience context banner */}
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: isProduction ? "#166534" : "#854d0e", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: isProduction ? "#166534" : "#854d0e" }} />
                {isProduction ? "Production View — Measurables, Stats & Grades" : "Profile View — Recruiting Background & Narrative"}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "3px" }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "22px", fontWeight: 700, color: "#0f0f0f" }}>{selected.name}</span>
                    <PosBadge pos={selected.pos} />
                    <StatusPill status={selected.portalStatus} />
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", color: "#166534", fontWeight: 700 }}>{selected.destination}</span>
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af" }}>
                    {selected.measurables.height} / {selected.measurables.weight}lbs · {selected.measurables.hometown} · {selected.measurables.highSchool} · {selected.eligibility} remaining
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {(() => {
                    const pff = selected.production?.pffGrade || selected.production?.pffGrade2024;
                    return pff ? (
                      <>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "4px" }}>Grade</div>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "32px", fontWeight: 700, color: pff >= 85 ? "#166534" : pff >= 70 ? "#854d0e" : "#991b1b", lineHeight: 1 }}>{pff}</div>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginTop: "2px" }}>{selected.production?.pffGrade2024 && !selected.production?.pffGrade ? "2024 season" : "2025 season"}</div>
                      </>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Stat strip — audience-aware */}
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }} className="stat-strip">
                {statBoxes.map(s => <StatBox key={s.label} {...s} />)}
              </div>
            </div>

            {/* Chart area */}
            <div style={C.mainBody} className="mobile-main-body">
              <div style={{ display: "flex", gap: "6px", marginBottom: "12px", alignItems: "center" }} className="tab-bar">
                {[["chart", "Rating History"], ["radar", "Skill Profile"], ["splits", "Game Splits"], ["career", "Career"], ["scouting", "Scouting Report"]].map(([k, l]) => (
                  <button key={k} style={C.tab(tab === k)} onClick={() => setTab(k)}>{l}</button>
                ))}
              </div>

              {/* RATING HISTORY — Under Development */}
              {tab === "chart" && (
                <div style={{ animation: "fadeIn 0.2s ease" }}>
                  <div style={{ ...C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "280px", gap: "12px" }}>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", color: "#d1d5db", textTransform: "uppercase", letterSpacing: "0.2em" }}>Under Development</div>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "18px", fontWeight: 700, color: "#9ca3af" }}>Rating History</div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", color: "#d1d5db", textAlign: "center", maxWidth: "320px", lineHeight: 1.6 }}>
                      Game-by-game grade tracking will appear here.<br />Data integration in progress.
                    </div>
                  </div>
                </div>
              )}

              {/* RADAR */}
              {tab === "radar" && selected.radar && (
                <div style={{ ...C.card, animation: "fadeIn 0.2s ease" }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>Skill Profile — {selected.name}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginBottom: "16px" }}>Position-adjusted composite across six core attributes</div>
                  <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                    <ResponsiveContainer width={260} height={260}>
                      <RadarChart data={selected.radar}>
                        <PolarGrid stroke="#e5e7eb" />
                        <PolarAngleAxis dataKey="stat" tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fill: "#6b7280" }} />
                        <Radar name="Skill" dataKey="val" stroke="#0f0f0f" fill="#0f0f0f" fillOpacity={0.06} strokeWidth={2} dot={{ r: 3, fill: "#0f0f0f" }} />
                        <Tooltip content={<CustomTooltip />} />
                      </RadarChart>
                    </ResponsiveContainer>
                    <div style={{ flex: 1 }}>
                      {selected.radar.map(r => (
                        <div key={r.stat} style={{ marginBottom: "10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#6b7280" }}>{r.stat}</span>
                            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 700, color: "#0f0f0f" }}>{r.val}</span>
                          </div>
                          <div style={{ height: "2px", background: "#f3f4f6", borderRadius: "1px" }}>
                            <div style={{ width: `${r.val}%`, height: "100%", background: "#0f0f0f", transition: "width 0.4s ease" }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* GAME SPLITS */}
              {tab === "splits" && selected.weeklyStats && (
                <div style={{ animation: "fadeIn 0.2s ease" }}>
                  <div style={C.card}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>Game Splits — Last 6 Appearances</div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginBottom: "14px" }}>Weekly performance breakdown · 2025 season</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={selected.weeklyStats} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="#f3f4f6" vertical={false} />
                        <XAxis dataKey="week" tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fill: "#d1d5db" }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, fill: "#d1d5db" }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        {selected.pos === "QB" && <Bar dataKey="yards" fill="#0f0f0f" radius={[2, 2, 0, 0]} opacity={0.85} name="Yards" />}
                        {(selected.pos === "EDGE" || selected.pos === "LB") && <>
                          <Bar dataKey="pressures" fill="#d1d5db" radius={[2, 2, 0, 0]} name="Pressures" />
                          <Bar dataKey="sacks" fill="#0f0f0f" radius={[2, 2, 0, 0]} name="Sacks" />
                        </>}
                        {selected.pos === "OT" && <>
                          <Bar dataKey="pressures" fill="#d1d5db" radius={[2, 2, 0, 0]} name="Pressures Allowed" />
                          <Bar dataKey="sacksAllowed" fill="#0f0f0f" radius={[2, 2, 0, 0]} name="Sacks Allowed" />
                        </>}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ ...C.card, marginTop: "8px", padding: 0, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8f7f4", borderBottom: "2px solid #0f0f0f" }}>
                          {getWeeklyHeaders(selected.pos).map(h => (
                            <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#0f0f0f", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selected.weeklyStats.map((s, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "8px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px", fontWeight: 700, color: "#0f0f0f" }}>{s.week}</td>
                            {getWeeklyRow(s, selected.pos).map((val, j) => (
                              <td key={j} style={{ padding: "8px 14px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "10px" }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SCOUTING REPORT — media only */}
              {tab === "scouting" && !isProduction && (
                <div style={{ ...C.card, animation: "fadeIn 0.2s ease" }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>Scouting Report</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginBottom: "16px" }}>
                    Source: {selected.recruiting.platformCommentary.source}
                  </div>

                  <div style={{ display: "flex", gap: "20px", marginBottom: "16px", flexWrap: "wrap" }}>
                    {[
                      { label: "Recruiting Class", value: selected.recruiting.classYear },
                      { label: "Stars", value: "★".repeat(selected.recruiting.stars247), accent: "#854d0e" },
                      { label: "Natl. Rank", value: selected.recruiting.nationalRank247 ? `#${selected.recruiting.nationalRank247}` : "—" },
                      { label: "Pos. Rank", value: selected.recruiting.posRank247 ? `#${selected.recruiting.posRank247}` : "—" },
                      { label: "State Rank", value: selected.recruiting.stateRank ? `#${selected.recruiting.stateRank} (${selected.recruiting.state})` : "—" },
                      { label: "Signed With", value: selected.recruiting.commitSchool },
                    ].map(s => <StatBox key={s.label} {...s} />)}
                  </div>

                  <div style={{ height: "1px", background: "#f3f4f6", margin: "16px 0" }} />

                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "8px" }}>Platform Analysis</div>
                  <p style={{ fontSize: "13px", color: "#374151", lineHeight: 1.7, marginBottom: "16px" }}>
                    {selected.recruiting.platformCommentary.text}
                  </p>

                  <div style={{ height: "1px", background: "#f3f4f6", margin: "16px 0" }} />

                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "8px" }}>Other Notable Offers</div>
                  <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                    {selected.recruiting.otherOffers.map(o => (
                      <span key={o} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#374151", border: "1px solid #e5e7eb", background: "#f8f7f4", padding: "3px 8px", borderRadius: "2px" }}>{o}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* CAREER HISTORY */}
              {tab === "career" && (
                <div style={{ ...C.card, animation: "fadeIn 0.2s ease" }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, marginBottom: "4px" }}>Career History</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginBottom: "20px" }}>Transfer history and program timeline</div>
                  <div style={{ position: "relative", paddingLeft: "20px" }}>
                    <div style={{ position: "absolute", left: "6px", top: 0, bottom: 0, width: "1px", background: "#e5e7eb" }} />
                    {(() => {
                      const stops = [];
                      // 1. Original signing
                      stops.push({
                        year: selected.recruiting.classYear,
                        school: selected.recruiting.commitSchool,
                        role: `Signed — ${selected.recruiting.classYear} Recruiting Class`,
                        note: `${selected.recruiting.stars247}★ · Natl. #${selected.recruiting.nationalRank247 || "—"} · ${selected.recruiting.posRank247 ? `#${selected.recruiting.posRank247} ${selected.pos}` : ""}`,
                      });
                      // 2. Interim schools (from players.js careerHistory array if present)
                      if (selected.careerHistory && selected.careerHistory.length > 0) {
                        selected.careerHistory.forEach(entry => {
                          stops.push({
                            year: entry.years || entry.year || "—",
                            school: entry.school,
                            role: entry.role || "Transfer Entry",
                            note: entry.note || "Entered NCAA Transfer Portal",
                          });
                        });
                      } else if (selected.prev && selected.prev !== selected.recruiting.commitSchool && selected.prev !== selected.destination) {
                        // Fallback: show prev school as interim if different from signing school and destination
                        stops.push({
                          year: selected.prevYears || "Transfer",
                          school: selected.prev,
                          role: "Transfer Entry",
                          note: selected.prevNote || "Entered NCAA Transfer Portal",
                        });
                      }
                      // 3. Current destination
                      stops.push({
                        year: "2026",
                        school: selected.destination,
                        role: "Current Program",
                        note: `${selected.portalStatus} · ${selected.eligibility} remaining`,
                        current: true,
                      });
                      return stops.map((stop, i) => (
                        <div key={i} style={{ display: "flex", gap: "16px", marginBottom: "20px", alignItems: "flex-start" }}>
                          <div style={{ position: "absolute", left: "2px", width: "9px", height: "9px", borderRadius: "50%", background: stop.current ? "#0f0f0f" : "#d1d5db", border: "2px solid #fff", marginTop: "3px" }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "3px" }}>
                              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af", flexShrink: 0 }}>{stop.year}</span>
                              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "14px", fontWeight: 700, color: "#0f0f0f" }}>{stop.school}</span>
                              {stop.current && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#166534", fontWeight: 700, background: "#f0fdf4", border: "1px solid #16a34a", padding: "1px 6px", borderRadius: "2px" }}>CURRENT</span>}
                            </div>
                            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#6b7280" }}>{stop.role}</div>
                            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#9ca3af", marginTop: "2px" }}>{stop.note}</div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              <div style={{ ...C.card, marginTop: "8px", display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "4px" }}>Representation</div>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, color: "#0f0f0f" }}>{selected.contact.agent}</span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: "#6b7280", marginLeft: "10px" }}>{selected.contact.agency}</span>
                  {selected.contact.note && (
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", marginTop: "3px" }}>{selected.contact.note}</div>
                  )}
                </div>
                <button style={{ background: "transparent", border: "1px solid #e5e7eb", color: "#6b7280", padding: "6px 16px", borderRadius: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase" }}>Contact →</button>
                <button style={{ background: "#0f0f0f", border: "none", color: "#fff", padding: "6px 16px", borderRadius: "2px", fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700 }}>+ Add to Board</button>
              </div>
            </div>
          </main>
        )}

        {/* ── MOBILE FEED ── */}
        <div className="mobile-feed" style={{ display: "none", background: "#fff", borderTop: "1px solid #e5e7eb", padding: "16px" }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Intelligence Feed</span>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#4ade80" }} />
              <span style={{ color: "#4ade80" }}>LIVE</span>
            </div>
          </div>
          {NEWS.slice(0, 4).map(item => (
            <div key={item.id} className="news-card" style={{ background: "#f8f7f4", borderLeft: `3px solid ${sentimentBar[item.sentiment]}`, borderRadius: "2px", padding: "10px 12px", marginBottom: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ background: "#e5e7eb", color: "#374151", fontFamily: "'IBM Plex Mono', monospace", fontSize: "7px", fontWeight: 700, padding: "2px 5px", borderRadius: "2px", textTransform: "uppercase" }}>{item.tag}</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#d1d5db" }}>{item.time}</span>
              </div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#0f0f0f", lineHeight: 1.4 }}>{item.headline}</div>
            </div>
          ))}
        </div>

        {/* ── RIGHT PANEL ── */}
        <aside style={C.right} className="right-panel">
          <div style={C.rightHeader}>
            <span>Intelligence Feed</span>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#4ade80" }} />
              <span style={{ color: "#4ade80" }}>LIVE</span>
            </div>
          </div>

          <div style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", display: "flex", gap: "3px", flexWrap: "wrap", flexShrink: 0 }}>
            {["ALL", "Transfer", "Recruiting", "NIL", "Injury", "Analysis"].map(f => (
              <button key={f} onClick={() => setFeedFilter(f)} style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: "7px", padding: "2px 6px",
                background: feedFilter === f ? "#0f0f0f" : "transparent",
                color: feedFilter === f ? "#fff" : "#9ca3af",
                border: feedFilter === f ? "1px solid #0f0f0f" : "1px solid #e5e7eb",
                borderRadius: "2px", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700,
              }}>{f}</button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
            {filteredNews.map(item => (
              <div key={item.id} className="news-card" style={{
                background: "#fff",
                border: `1px solid #f3f4f6`,
                borderLeft: `3px solid ${sentimentBar[item.sentiment]}`,
                borderRadius: "2px", padding: "10px 12px", marginBottom: "6px",
                cursor: "pointer", transition: "background 0.1s",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                  <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                    <span style={{ background: "#f3f4f6", color: "#374151", fontFamily: "'IBM Plex Mono', monospace", fontSize: "7px", fontWeight: 700, padding: "2px 5px", borderRadius: "2px", textTransform: "uppercase", letterSpacing: "0.1em" }}>{item.tag}</span>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af" }}>{item.source}</span>
                  </div>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#d1d5db" }}>{item.time}</span>
                </div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "#0f0f0f", lineHeight: 1.4, marginBottom: "4px" }}>{item.headline}</div>
                <div style={{ fontSize: "10px", color: "#6b7280", lineHeight: 1.5 }}>{item.body}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: "10px 12px", borderTop: "1px solid #f3f4f6", background: "#f8f7f4", flexShrink: 0 }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "6px" }}>Portal Alerts</div>
            {[
              { msg: "Lagway — enrolled at Baylor", color: "#166534", time: "Jan 18" },
              { msg: "Umanmielen — LSU buyout confirmed", color: "#854d0e", time: "Jan 21" },
            ].map((a, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "9px", color: a.color, fontWeight: 600 }}>{a.msg}</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "8px", color: "#d1d5db", marginLeft: "6px" }}>{a.time}</span>
              </div>
            ))}
          </div>
        </aside>

      </div>
    </div>
  );
}
